import { useEffect, useState } from 'react'
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { FeedbackDialog } from '@/components/feedback-dialog'
import { PageContainer } from '@/components/shared/page-container'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { ThemeToggle } from '@/components/ThemeToggle'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { getDefaultRouteForRole } from '@/lib/auth'
import { getCurrentUserRole, signOutUser } from '@/lib/api'
import { getErrorMessage } from '@/lib/error-messages'
import { queryKeys } from '@/lib/query-keys'
import { Footer } from './footer'
import { authEvents } from '@/lib/auth-events'
import { cn } from '@/lib/utils'

const trainerLinks = [
  { to: '/trainer/meine-kurse', label: 'Meine Kurse' },
  { to: '/trainer/customers', label: 'Teilnehmer' },
]
const memberLinks = [
  { to: '/dashboard', label: 'Übersicht' },
  { to: '/kurse', label: 'Kurse' },
  { to: '/kalender', label: 'Kalender' },
  { to: '/meine-kurse', label: 'Meine Kurse' },
  { to: '/abo', label: 'Abo' },
  { to: '/umfragen', label: 'Umfragen' },
]

export function AppLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [feedbackOpen, setFeedbackOpen] = useState(false)

  const profileQuery = useQuery({
    queryKey: queryKeys.currentUserProfile,
    queryFn: getCurrentUserRole,
    retry: false,
    refetchOnWindowFocus: false,
  })

  useEffect(
    () =>
      authEvents.subscribe(() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.currentUserProfile })
      }),
    [queryClient],
  )

  const logoutMutation = useMutation({
    mutationFn: signOutUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUserProfile })
      toast.success('Erfolgreich ausgeloggt.')
      navigate('/login', { replace: true })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  if (profileQuery.isLoading) {
    return (
      <PageContainer className="py-12">
        <Skeleton className="h-8 w-64" />
      </PageContainer>
    )
  }

  const profile = profileQuery.data
  const isCalendarPage = location.pathname === '/kalender'
  if (!profile || !profile.is_active) {
    return <Navigate to="/login" replace />
  }

  if (profile.role === 'admin') {
    return <Navigate to={getDefaultRouteForRole(profile.role)} replace />
  }

  if (profile.role === 'trainer' && !location.pathname.startsWith('/trainer')) {
    return <Navigate to="/trainer/meine-kurse" replace />
  }

  if (profile.role === 'member' && location.pathname.startsWith('/trainer')) {
    return <Navigate to="/dashboard" replace />
  }

  const links = profile.role === 'trainer' ? trainerLinks : memberLinks
  const homeRoute = getDefaultRouteForRole(profile.role)
  const roleLabel = profile.role === 'trainer' ? 'Trainer' : 'Teilnehmer'
  const isMember = profile.role === 'member'

  const initials = profile.full_name
    ? profile.full_name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
    : 'U'

  return (
    <div className="h-dvh overflow-hidden font-body text-foreground flex flex-col">
      <header className="glass sticky top-0 z-50 border-b border-border/50 shadow-lg">
        <PageContainer className="flex h-16 items-center justify-between">
          <div className="flex items-center gap-8">
            <Link
              to={homeRoute}
              className="font-headline flex items-center gap-2 tracking-wider transition-opacity hover:opacity-90"
            >
              <img src="/motionlab.png" alt="motionLAB Logo" className="h-10 w-auto object-contain" />
              <div className="flex items-center text-lg font-bold">
                <span className="text-foreground">motion</span>
                <span className="text-gradient ml-[-1px]">LAB</span>
                <span className="ml-2 text-xs font-medium uppercase tracking-widest text-muted-foreground">
                  {roleLabel}
                </span>
              </div>
            </Link>

            <nav className="hidden items-center gap-1 md:flex xl:gap-3">
              {links.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={({ isActive }) =>
                    cn(
                      'rounded-md px-3 py-1.5 font-nav text-sm font-medium transition-all hover:text-primary lg:whitespace-nowrap',
                      isActive ? 'bg-primary/20 text-primary' : 'text-foreground/80 hover:bg-muted',
                    )
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              {isMember && (
                <button
                  type="button"
                  onClick={() => setFeedbackOpen(true)}
                  className="rounded-md px-3 py-1.5 font-nav text-sm font-medium text-foreground/80 transition-all hover:bg-muted hover:text-primary"
                >
                  Feedback
                </button>
              )}
            </nav>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" size="sm" className="md:hidden">
                  Menü
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {links.map((link) => (
                  <DropdownMenuItem key={link.to} asChild>
                    <Link to={link.to}>{link.label}</Link>
                  </DropdownMenuItem>
                ))}
                {isMember && (
                  <DropdownMenuItem onSelect={() => setFeedbackOpen(true)}>Feedback</DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/profil">Mein Profil</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/handbuch">Handbuch</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => logoutMutation.mutate()}
                  disabled={logoutMutation.isPending}
                >
                  {logoutMutation.isPending ? 'Logout ...' : 'Logout'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="relative hidden h-10 w-10 rounded-full md:flex"
                  aria-label="Benutzermenü öffnen"
                >
                  <Avatar className="h-10 w-10 border border-border/50">
                    <AvatarImage src={profile.avatar_url || undefined} alt={profile.full_name || ''} className="object-cover" />
                    <AvatarFallback className="bg-primary/10 text-primary">{initials}</AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem asChild>
                  <Link to="/profil">Mein Profil</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/handbuch">Handbuch</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => logoutMutation.mutate()}
                  disabled={logoutMutation.isPending}
                >
                  {logoutMutation.isPending ? 'Logout ...' : 'Logout'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              type="button"
              variant="outline"
              size="sm"
              className="md:hidden hidden"
              onClick={() => logoutMutation.mutate()}
              disabled={logoutMutation.isPending}
            >
              Logout
            </Button>
          </div>
        </PageContainer>
      </header>
      <main className="flex-1 min-h-0">
        <PageContainer
          data-scroll-container="app-layout"
          className={cn(
            'h-full overflow-y-auto py-8',
            isCalendarPage && 'overflow-y-hidden py-2',
          )}
        >
          <Outlet context={profile} />
        </PageContainer>
      </main>
      <div className="shrink-0"><Footer showGitHash /></div>
      {isMember && <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />}
    </div>
  )
}
